import { useRef } from 'react';
import { Select } from 'antd';
import { SelectProps } from 'antd/lib/select';
import { addAllTermAndSort } from 'constants/GlobalConfig';

const { Option } = Select;

interface TermSelectProps extends Omit<SelectProps<string>, 'children'> {
    /**
     * 年度リスト
     */
    termList: Term[];
    /**
     * 選択された年度UUID
     */
    value?: string;
    /**
     * 年度変更時のコールバック
     */
    onChange?: (termUuid: string) => void;
    /**
     * プレースホルダー
     */
    placeholder?: string;
    /**
     * 無効化フラグ
     */
    disabled?: boolean;
    /**
     * ローディング状態
     */
    loading?: boolean;
}

/**
 * 年度選択用の共通Selectコンポーネント
 * 選択中のオプションを中央にスクロールする機能を持つ
 */
const TermSelect: React.FC<TermSelectProps> = ({
    termList,
    value,
    onChange,
    placeholder = '年度を選択してください',
    disabled = false,
    loading = false,
    ...restProps
}) => {
    // 年度セレクトのドロップダウン要素への参照
    const dropdownRef = useRef<HTMLDivElement>(null);

    /**
     * 年度セレクトのドロップダウンが開いた時、選択中のオプションを中央にスクロールする
     * requestAnimationFrameを使用してDOMレンダリング完了後にスクロール処理を実行
     * スクロールイベントが外部に伝播しないように制御
     */
    function handleDropdownVisibleChange(open: boolean) {
        if (!open) return;

        requestAnimationFrame(() => {
            const selectedOption = dropdownRef.current?.querySelector(
                '.ant-select-item-option-selected',
            ) as HTMLElement;

            if (selectedOption) {
                setTimeout(() => {
                    const x = window.scrollX;
                    const y = window.scrollY;

                    selectedOption.scrollIntoView({
                        block: 'center',
                        inline: 'nearest',
                    });
                    window.scrollTo(x, y);
                }, 100);
            }
        });
    }

    /**
     * ドロップダウン内のスクロールイベントが外部に伝播しないようにする
     */
    function handleDropdownWheel(e: React.WheelEvent<HTMLDivElement>) {
        e.stopPropagation();
        e.isPropagationStopped();
    }

    return (
        <Select<string>
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            disabled={disabled}
            loading={loading}
            onDropdownVisibleChange={handleDropdownVisibleChange}
            dropdownRender={(menu) => (
                <div ref={dropdownRef} onWheel={handleDropdownWheel}>
                    {menu}
                </div>
            )}
            virtual={false}
            {...restProps}
        >
            {addAllTermAndSort(termList).map((term, index) => (
                <Option key={index} value={term.uuid}>
                    {term.name}
                </Option>
            ))}
        </Select>
    );
};

export default TermSelect;
